import { VisaCard } from '@/components/payment-method/visa-card';
import { MastercardCard } from '@/components/payment-method/mastercard-card';
import { AmexCard } from '@/components/payment-method/amex-card';
import { TimeFromUnix } from '@/components/time-from-unix';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Fragment } from 'react';

interface PaymentMethodProps {
  method: {
    brand: 'visa' | 'mastercard' | 'amex';
    last4: string;
    exp_month: number;
    exp_year: number;
    created: number;
    default?: boolean;
  };
  format?: 'default' | 'slim' | 'compact';
  selected?: boolean;
  selectable?: boolean;
}

export function PaymentMethod({ method, format = 'default', selected, selectable }: PaymentMethodProps) {
  const formattedMonth = method.exp_month.toString().padStart(2, '0');
  const formattedYear = method.exp_year.toString().slice(-2);

  return (
    <div className={clsx(`rounded-md bg-gray-50 px-6 py-5 sm:flex sm:items-start sm:justify-between border border-gray-200 `, {
      'border border-indigo-600 shadow-md': selected,
      'cursor-pointer': !selected && selectable
    })}>
      <h4 className="sr-only">{method.brand}</h4>
      <div className="sm:flex sm:items-start">
        {method.brand === 'visa' && <VisaCard />}
        {method.brand === 'mastercard' && <MastercardCard />}
        {method.brand === 'amex' && <AmexCard />}
        <div className="mt-3 sm:ml-4 sm:mt-0">
        <div className="text-sm font-medium text-gray-900">Ending with {method.last4}</div>
          <div className="mt-1 text-sm text-gray-600 sm:flex sm:items-center">
            <div>Expires {formattedMonth}/{formattedYear}</div>
            {format === "default" && (
              <>
                <span aria-hidden="true" className="hidden sm:mx-2 sm:inline">&middot;</span>
                <div className="mt-1 sm:mt-0">
                  Added <TimeFromUnix unixTimestamp={method.created} />
                </div>
              </>
            )}
            {method.default && (format === "default" || format === "slim") &&
              <>
                <span aria-hidden="true" className="hidden sm:mx-2 sm:inline">&middot;</span>
                <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">Default</span>
              </>
            }
          </div>
        </div>
      </div>
      {format === "default" && (
        <div className="mt-4 sm:ml-6 sm:mt-0 sm:shrink-0">
          <Menu
            as="div"
            className="relative inline-block text-left"
          >
            <div>
              <Menu.Button className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                Edit
                <ChevronDownIcon
                  className="-mr-1 ml-2 h-5 w-5"
                  aria-hidden="true"
                />
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {method.default ? null : (
                  <div className="">
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          className={clsx(
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700',
                            'block px-4 py-2 text-sm'
                          )}
                        >
                          Make Default
                        </a>
                      )}
                    </Menu.Item>
                  </div>
                )}
                <div className="">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={clsx(
                          active
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700',
                          'block px-4 py-2 text-sm w-full text-left'
                        )}
                      >
                        Delete
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      )}
    </div>
  );
}